import React, { useState } from 'react';
import useHideNames from '../hooks/useHideNames';
import { maskName } from '../util/privacy';
import styled, { css } from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const columns = css`
  display: grid;
  grid-template-columns: clamp(48px, 7vw, 120px) minmax(0, 1fr) clamp(130px, 14vw, 240px);
  align-items: center;
  gap: clamp(8px, 1.3vw, 24px);
  padding: 12px clamp(12px, 1.6vw, 28px);
  @media (max-width: 600px) {
    grid-template-columns: 32px minmax(0, 1fr) 86px;
    gap: 8px;
    padding: 10px 8px;
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  min-height: 0;
  margin-top: 2rem;
  font-family: 'Open Sans', sans-serif;
  color: #fff8eb;
  & *, & *::before, & *::after { box-sizing: border-box; }
  @media (max-width: 600px) { width: 96%; }
`;

const Header = styled.div`
  ${columns}
  flex-shrink: 0;
  margin-right: 6px;
  border-inline: 3px solid transparent;
  color: #ffe69a;
  font-size: clamp(12px, 1.25vw, 22px);
  font-weight: 700;
  text-transform: uppercase;
  text-align: left;
  & > span { display: flex; align-items: center; gap: 8px; min-width: 0; }
  & > span:last-child { justify-content: flex-end; }
  @media (max-width: 600px) {
    & > span { gap: 4px; flex-wrap: wrap; }
  }
`;

const EyeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 1px solid #d6ad5e99;
  border-radius: 50%;
  background: #46000066;
  color: #ffe69a;
  cursor: pointer;
  & > svg { width: 22px; height: 22px; }
  &:hover { background: #ffe69a22; border-color: #ffe69a; }
  &:focus-visible { outline: 2px solid #fff1bb; outline-offset: 3px; }
  @media (pointer: coarse) { width: 44px; height: 44px; }
`;

const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
`;

const Row = styled.div`
  ${columns}
  min-height: 64px;
  margin-bottom: 12px;
  border: 3px solid;
  border-color: #fff0b2 #b18236 #c99d4b #ebcb83;
  border-radius: 14px;
  background: ${({ theme }) => theme.raffleItemBackground || theme.itemRankBackground || 'rgba(192, 0, 10, .4)'};
  box-shadow: inset 0 0 0 1px #7d4926, inset 0 5px 8px -3px #35000099, inset 0 -1px 0 #ffeabc99;
  font-size: clamp(18px, 1.7vw, 30px);
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  & > .participant-name { min-width: 0; text-align: left; overflow-wrap: anywhere; }
  & > .participant-points { text-align: right; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  @media (max-width: 600px) { font-size: 16px; }
`;

const rankFinishes = {
  1: css`
    color: #673900;
    background: linear-gradient(145deg, #fff4c9 0%, #f6d571 36%, #e3b34b 49%, #bd8126 51%, #f4d37c 100%);
    border: 1px solid #ffe9a2;
    box-shadow: inset 0 1px 0 #fff9dd, 0 2px 0 #795013;
    text-shadow: 0 1px #fff0b888;
  `,
  2: css`
    color: #374452;
    background: linear-gradient(145deg, #f9fcff 0%, #d8e1e9 36%, #bfcbd6 49%, #8798a9 51%, #e4eaf0 100%);
    border: 1px solid #e6eef6;
    box-shadow: inset 0 1px 0 #ffffff, 0 2px 0 #56616e;
    text-shadow: 0 1px #ffffff88;
  `,
  3: css`
    color: #542b13;
    background: linear-gradient(145deg, #ffe0c1 0%, #dda577 36%, #c18a55 49%, #985b30 51%, #e9b17e 100%);
    border: 1px solid #e9bb8e;
    box-shadow: inset 0 1px 0 #ffead1, 0 2px 0 #623614;
    text-shadow: 0 1px #ffddb888;
  `,
};

const Rank = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: start;
  min-width: 36px;
  min-height: 36px;
  padding: 4px;
  border-radius: 8px;
  color: #ffe69a;
  background: #33000055;
  ${({ $rank }) => rankFinishes[$rank]}
  font-size: .85em;
  @media (max-width: 600px) { min-width: 28px; min-height: 30px; }
`;

export default function TournamentRankingTable({ participants }) {
  const [hideNames, toggleHideNames] = useHideNames();
  const [hidePoints, setHidePoints] = useState(false);
  const NamesIcon = hideNames ? FiEyeOff : FiEye;
  const PointsIcon = hidePoints ? FiEyeOff : FiEye;

  return (
    <Table role="table" aria-label="Clasificación del torneo">
      <Header role="row">
        <span role="columnheader">Pos.</span>
        <span role="columnheader">
          Participantes
          <EyeButton type="button" aria-label={hideNames ? 'Mostrar nombres' : 'Ocultar nombres'} aria-pressed={!hideNames} onClick={toggleHideNames}>
            <NamesIcon aria-hidden="true" />
          </EyeButton>
        </span>
        <span role="columnheader">
          Puntos
          <EyeButton type="button" aria-label={hidePoints ? 'Mostrar puntos' : 'Ocultar puntos'} aria-pressed={!hidePoints} onClick={() => setHidePoints(hidden => !hidden)}>
            <PointsIcon aria-hidden="true" />
          </EyeButton>
        </span>
      </Header>
      <Body role="rowgroup" className="custom-scroll">
        {participants.slice(0, 50).map(item => {
          const name = maskName(item.firstName, item.lastName1, hideNames);
          return (
            <Row role="row" key={item.rank}>
              <Rank role="cell" $rank={item.rank}>{item.rank}</Rank>
              <span role="cell" className="participant-name">{name}</span>
              <span role="cell" className="participant-points" aria-label={hidePoints ? 'Puntos ocultos' : undefined}>{hidePoints ? '••••' : item.total}</span>
            </Row>
          );
        })}
      </Body>
    </Table>
  );
}
